import React, { useEffect, useState, useCallback } from "react";
import { api, formatMXN } from "../lib/api";
import { useConfirm } from "../context/ConfirmContext";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Search, Settings2, Users, ChevronDown, ChevronRight, CheckCircle2, Banknote, Percent } from "lucide-react";
import { toast } from "sonner";

function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(s) {
  if (!s) return "—";
  const [y, m] = s.split("-");
  const names = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

export default function AdminTeams() {
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState(currentMonthKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // pid -> detail data
  const [editing, setEditing] = useState(null); // promoter being edited

  const load = useCallback(async (s, m) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (s) params.set("search", s);
      if (m) params.set("month", m);
      const { data } = await api.get(`/admin/teams?${params.toString()}`);
      setData(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load("", month); /* eslint-disable-next-line */ }, [month]);

  const onSearch = (e) => {
    e.preventDefault();
    load(search.trim(), month);
  };

  const toggleExpand = async (pid) => {
    if (expanded[pid]) {
      setExpanded((prev) => { const n = { ...prev }; delete n[pid]; return n; });
      return;
    }
    try {
      const { data } = await api.get(`/admin/teams/${pid}?month=${month}`);
      setExpanded((prev) => ({ ...prev, [pid]: data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar detalle");
    }
  };

  const refresh = () => load(search.trim(), month);

  return (
    <div className="bg-white border border-border rounded-2xl p-6" data-testid="admin-teams-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-200 via-emerald-300 to-emerald-500 grid place-items-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-display text-2xl tracking-tight">Equipos de promotores</h3>
            <p className="text-xs text-muted-foreground">
              Configura el % de bonus que cada promotor gana por las ventas de su grupo de referidos.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Mes:</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="teams-month-input" />
        </div>
      </div>

      <form onSubmit={onSearch} className="flex gap-2 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar promotor por nombre o teléfono…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="teams-search-input"
          />
        </div>
        <Button type="submit" className="btn-primary rounded-full" data-testid="teams-search-btn">Buscar</Button>
        {search && (
          <Button type="button" variant="outline" onClick={() => { setSearch(""); load("", month); }} data-testid="teams-clear-btn">Limpiar</Button>
        )}
      </form>

      {loading || !data ? (
        <div className="text-muted-foreground text-sm">Cargando…</div>
      ) : data.promoters.length === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center border-2 border-dashed border-border rounded-xl" data-testid="teams-empty">
          {search ? `Sin resultados para "${search}".` : "Aún no hay promotores."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="teams-list">
          {data.promoters.map((p) => (
            <div key={p.id} className="border border-border rounded-xl overflow-hidden" data-testid={`team-card-${p.id}`}>
              <button
                onClick={() => toggleExpand(p.id)}
                className="w-full text-left px-4 py-3 hover:bg-[#FAFAFA] transition-colors flex items-center gap-3"
                data-testid={`team-toggle-${p.id}`}
              >
                {expanded[p.id] ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-5 gap-3 items-center">
                  <div className="md:col-span-2">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{p.phone}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground">Equipo</div>
                    <div className="text-sm font-medium">{p.team_active_size}/{p.team_size}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground">Ventas equipo · {monthLabel(data.month)}</div>
                    <div className="text-sm font-medium">{formatMXN(p.team_month_commissions_total)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div>
                      <div className="text-[10px] text-muted-foreground">% bonus</div>
                      <div className="text-sm font-medium">
                        {p.referral_bonus_pct}% <span className="text-[10px] text-muted-foreground">({p.referral_bonus_mode === "monthly" ? "mes" : "única"})</span>
                      </div>
                    </div>
                    {p.calculated_bonus > 0 && (
                      <Badge className="bg-amber-200 text-amber-900 border border-amber-300">
                        {formatMXN(p.calculated_bonus)}
                      </Badge>
                    )}
                  </div>
                </div>
                <span
                  onClick={(e) => { e.stopPropagation(); setEditing(p); }}
                  className="text-xs text-[#7C3AED] hover:bg-[#F5F3FF] rounded-full px-3 py-1.5 inline-flex items-center gap-1 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  data-testid={`team-edit-${p.id}`}
                >
                  <Settings2 className="w-3.5 h-3.5" /> Editar %
                </span>
              </button>

              {expanded[p.id] && (
                <TeamDetail
                  pid={p.id}
                  detail={expanded[p.id]}
                  onRefresh={refresh}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <ReferralConfigDialog
        promoter={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refresh(); }}
      />
    </div>
  );
}

function TeamDetail({ pid, detail, onRefresh }) {
  const confirm = useConfirm();
  const markOneTimePaid = async () => {
    if (!await confirm(`¿Marcar el bonus de única vez como YA pagado para "${detail.promoter_name}"? Después podrás resetearlo cambiando el modo.`)) return;
    try {
      await api.post(`/admin/promoters/${pid}/referral-bonus/mark-paid`);
      toast.success("Bonus marcado como pagado");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="border-t border-border bg-[#FAFAFA] px-4 py-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Cell label="Promotores referidos" value={detail.team_size} sub={`${detail.team_active_size} activos`} />
        <Cell label="Ventas del equipo" value={formatMXN(detail.team_month_commissions_total)} sub={monthLabel(detail.month)} />
        <Cell label="% asignado" value={`${detail.referral_bonus_pct}%`} sub={detail.referral_bonus_mode === "monthly" ? "mes a mes" : (detail.referral_bonus_one_time_paid ? "única vez · pagado" : "única vez")} />
        <Cell label="Bonus calculado" value={formatMXN(detail.my_calculated_bonus)} accent />
      </div>

      {detail.referral_bonus_mode === "one_time" && !detail.referral_bonus_one_time_paid && detail.my_calculated_bonus > 0 && (
        <div className="mb-3 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <div className="text-xs text-amber-900">
            Este promotor tiene un bonus de <strong>única vez</strong> pendiente de {formatMXN(detail.my_calculated_bonus)}.
          </div>
          <Button size="sm" className="btn-primary rounded-full" onClick={markOneTimePaid} data-testid={`team-onetime-pay-${pid}`}>
            <Banknote className="w-3.5 h-3.5 mr-1" /> Marcar como pagado
          </Button>
        </div>
      )}

      {detail.referrals.length === 0 ? (
        <div className="text-xs text-muted-foreground py-4 text-center">Este promotor aún no ha invitado a nadie.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground border-b border-border">
            <tr><th className="py-2">Promotor referido</th><th>Tel</th><th>Comercios act.</th><th>Comisiones del mes</th><th>Aporta al bonus</th></tr>
          </thead>
          <tbody>
            {detail.referrals.map((r) => {
              const myCut = (r.month_commissions_amount * (detail.referral_bonus_pct || 0)) / 100;
              return (
                <tr key={r.promoter_id} className="border-b border-border" data-testid={`team-detail-row-${r.promoter_id}`}>
                  <td className="py-2"><div className="font-medium">{r.name}</div>{!r.is_active && <div className="text-[10px] text-rose-600">Inactivo</div>}</td>
                  <td className="font-mono text-xs">{r.phone}</td>
                  <td>{r.active_affiliates}</td>
                  <td className="font-medium">{formatMXN(r.month_commissions_amount)}</td>
                  <td className="font-medium text-emerald-700 inline-flex items-center gap-1"><Percent className="w-3 h-3" />{formatMXN(myCut)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const Cell = ({ label, value, sub, accent }) => (
  <div className={`rounded-lg p-3 border ${accent ? "bg-gradient-to-br from-amber-100 to-amber-200 border-amber-300 text-amber-900" : "bg-white border-border"}`}>
    <div className="overline text-[10px] text-muted-foreground">{label}</div>
    <div className="font-display text-xl mt-0.5">{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

function ReferralConfigDialog({ promoter, onClose, onSaved }) {
  const [pct, setPct] = useState(0);
  const [mode, setMode] = useState("monthly");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (promoter) {
      setPct(promoter.referral_bonus_pct || 0);
      setMode(promoter.referral_bonus_mode || "monthly");
    }
  }, [promoter]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/promoters/${promoter.id}/referral-config`, {
        referral_bonus_pct: parseFloat(pct) || 0,
        referral_bonus_mode: mode,
      });
      toast.success("Configuración guardada");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!promoter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="referral-config-dialog">
        <DialogHeader>
          <DialogTitle>Editar % de bonus de equipo</DialogTitle>
        </DialogHeader>
        {promoter && (
          <div className="space-y-4">
            <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-xl p-3 text-sm">
              <div className="font-medium">{promoter.name}</div>
              <div className="text-xs text-muted-foreground">{promoter.phone} · {promoter.team_size} promotores en su equipo</div>
            </div>

            <div>
              <Label className="text-sm">Porcentaje (%) del total de ventas del equipo</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                placeholder="10"
                data-testid="referral-config-pct"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                Ejemplo: 10% significa que si su equipo produjo $1,000 en comisiones, este promotor gana $100 como bonus.
              </div>
            </div>

            <div>
              <Label className="text-sm">Modalidad de pago</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setMode("monthly")}
                  className={`text-left p-3 rounded-lg border-2 transition-all ${mode === "monthly" ? "border-[#7C3AED] bg-[#F5F3FF]" : "border-border bg-white hover:border-[#C4B5FD]"}`}
                  data-testid="referral-config-mode-monthly"
                >
                  <div className="flex items-center gap-2">
                    {mode === "monthly" && <CheckCircle2 className="w-4 h-4 text-[#7C3AED]" />}
                    <span className="font-medium text-sm">Mes a mes</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">El bonus se calcula y paga todos los meses (recurrente).</div>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("one_time")}
                  className={`text-left p-3 rounded-lg border-2 transition-all ${mode === "one_time" ? "border-[#7C3AED] bg-[#F5F3FF]" : "border-border bg-white hover:border-[#C4B5FD]"}`}
                  data-testid="referral-config-mode-onetime"
                >
                  <div className="flex items-center gap-2">
                    {mode === "one_time" && <CheckCircle2 className="w-4 h-4 text-[#7C3AED]" />}
                    <span className="font-medium text-sm">Única vez</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">Pago único. Lo marcas como pagado y se cierra.</div>
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={save} disabled={busy} className="btn-primary rounded-full" data-testid="referral-config-save">
                {busy ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
